import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTemplate } from '@/contexts/TemplateContext'
import type { PublicProfile } from '@/types'
import { BookingModal } from '@/components/booking/BookingModal'
import { Gallery } from '@/components/gallery'

interface TradeTemplateProps {
    profile: PublicProfile
}

export function TradeTemplate({ profile }: TradeTemplateProps) {
    const { t } = useTranslation('public')
    const { colors, typography } = useTemplate()
    const [isBookingOpen, setIsBookingOpen] = useState(false)

    return (
        <div
            className="min-h-screen"
            style={{
                fontFamily: typography.body,
                backgroundColor: colors.colors.background,
                color: colors.colors.text,
            }}
        >
            {/* Top Banner with Trust Indicators */}
            <div
                className="px-4 py-2 text-center text-xs md:text-sm"
                style={{ backgroundColor: colors.colors.accent }}
            >
                <span className="font-medium" style={{ color: colors.colors.primary }}>
                    ⭐ {t('template.trade.trustedBanner')}
                </span>
            </div>

            {/* Header */}
            <header
                className="px-4 py-6 md:px-6 md:py-8 lg:px-8"
                style={{ backgroundColor: colors.colors.primary }}
            >
                <div className="max-w-5xl mx-auto">
                    <div className="flex items-center gap-4">
                        {profile.profile_image ? (
                            <img
                                src={profile.profile_image}
                                alt={profile.business_name}
                                className="w-16 h-16 md:w-20 md:h-20 rounded-full border-2 object-cover"
                                style={{ borderColor: colors.colors.accent }}
                            />
                        ) : (
                            <div
                                className="w-16 h-16 md:w-20 md:h-20 rounded-full border-2 flex items-center justify-center"
                                style={{ borderColor: colors.colors.accent, backgroundColor: colors.colors.secondary }}
                            >
                                <span className="text-2xl font-bold" style={{ color: colors.colors.primary }}>
                                    {profile.business_name[0]}
                                </span>
                            </div>
                        )}
                        <div className="flex-1">
                            <h1
                                className="text-xl md:text-2xl font-bold"
                                style={{ fontFamily: typography.heading, color: colors.colors.secondary }}
                            >
                                {profile.business_name}
                            </h1>
                            {profile.tagline && (
                                <p className="text-sm opacity-90 mt-1" style={{ color: colors.colors.secondary }}>
                                    {profile.tagline}
                                </p>
                            )}
                            {profile.location && (
                                <p className="text-xs mt-1 opacity-80" style={{ color: colors.colors.secondary }}>
                                    📍 {profile.location}
                                </p>
                            )}
                        </div>
                        {/* Quick Contact Icons */}
                        <div className="flex gap-2">
                            {profile.phone && (
                                <a
                                    href={`tel:${profile.phone}`}
                                    className="w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center"
                                    style={{ backgroundColor: colors.colors.accent }}
                                    title="Call Now"
                                >
                                    <span className="text-lg md:text-xl">📞</span>
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* Hero CTA Section */}
            <div
                className="px-4 py-8 md:px-6 md:py-10 lg:px-8 text-center"
                style={{ backgroundColor: colors.colors.secondary }}
            >
                <h2
                    className="text-2xl md:text-3xl font-bold mb-3"
                    style={{ fontFamily: typography.heading, color: colors.colors.primary }}
                >
                    {t('template.trade.needHelp')}
                </h2>
                <p className="mb-6 text-sm md:text-base" style={{ color: colors.colors.textMuted }}>
                    {t('template.trade.reliableService')}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 sm:justify-center">
                    {profile.phone && (
                        <a
                            href={`tel:${profile.phone}`}
                            className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg font-bold text-lg transition-transform hover:scale-105"
                            style={{
                                backgroundColor: colors.colors.primary,
                                color: colors.colors.secondary,
                            }}
                        >
                            📞 {profile.phone}
                        </a>
                    )}
                    {profile.email && (
                        <a
                            href={`mailto:${profile.email}`}
                            className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg font-bold text-lg transition-transform hover:scale-105"
                            style={{
                                backgroundColor: colors.colors.background,
                                color: colors.colors.primary,
                                border: `2px solid ${colors.colors.primary}`,
                            }}
                        >
                            ✉️ {t('template.trade.sendEmail')}
                        </a>
                    )}
                </div>
            </div>

            {/* Bio */}
            {profile.bio && (
                <section className="px-4 py-6 md:px-6 md:py-8 lg:px-8 lg:max-w-5xl lg:mx-auto">
                    <h2
                        className="text-xl md:text-2xl font-bold mb-4 flex items-center gap-2"
                        style={{ fontFamily: typography.heading, color: colors.colors.primary }}
                    >
                        {t('template.trade.aboutUs')}
                    </h2>
                    <p className="leading-relaxed" style={{ lineHeight: 1.7 }}>
                        {profile.bio}
                    </p>
                </section>
            )}

            {/* Services - Card Grid Style */}
            {profile.services && profile.services.length > 0 && (
                <section className="px-4 py-6 md:px-6 md:py-8 lg:px-8 lg:max-w-5xl lg:mx-auto border-t" style={{ borderColor: colors.colors.secondary }}>
                    <h2
                        className="text-xl md:text-2xl font-bold mb-6"
                        style={{ fontFamily: typography.heading, color: colors.colors.primary }}
                    >
                        {t('template.trade.ourServices')}
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {profile.services.map((service) => (
                            <div
                                key={service.id}
                                className="p-4 rounded-lg shadow-sm border"
                                style={{
                                    backgroundColor: colors.colors.background,
                                    borderColor: colors.colors.secondary,
                                }}
                            >
                                <h3
                                    className="font-bold text-base md:text-lg mb-2"
                                    style={{ color: colors.colors.text }}
                                >
                                    {service.name}
                                </h3>
                                {service.description && (
                                    <p
                                        className="text-sm mb-3"
                                        style={{ color: colors.colors.textMuted }}
                                    >
                                        {service.description}
                                    </p>
                                )}
                                <div className="flex items-center justify-between">
                                    {service.price_display !== 'hidden' && service.price && (
                                        <span
                                            className="text-lg font-bold"
                                            style={{ color: colors.colors.accent }}
                                        >
                                            {service.price_display === 'from' && 'From '}${service.price.toFixed(2)}
                                        </span>
                                    )}
                                    {service.duration_minutes && (
                                        <span
                                            className="text-xs"
                                            style={{ color: colors.colors.textMuted }}
                                        >
                                            ⏱ ~{service.duration_minutes} min
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Trust Badges / Features */}
            <section
                className="px-4 py-6 md:px-6 md:py-8 lg:px-8 border-t"
                style={{ backgroundColor: colors.colors.secondary, borderColor: colors.colors.secondary }}
            >
                <div className="max-w-5xl mx-auto">
                    <div className="grid grid-cols-3 gap-4 text-center">
                        <div className="flex flex-col items-center">
                            <span className="text-2xl md:text-3xl mb-1">✓</span>
                            <span className="text-xs font-medium" style={{ color: colors.colors.textMuted }}>
                                {t('template.trade.licensed')}
                            </span>
                        </div>
                        <div className="flex flex-col items-center">
                            <span className="text-2xl md:text-3xl mb-1">✓</span>
                            <span className="text-xs font-medium" style={{ color: colors.colors.textMuted }}>
                                {t('template.trade.insured')}
                            </span>
                        </div>
                        <div className="flex flex-col items-center">
                            <span className="text-2xl md:text-3xl mb-1">✓</span>
                            <span className="text-xs font-medium" style={{ color: colors.colors.textMuted }}>
                                {t('template.trade.freeEstimates')}
                            </span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Gallery - Work Examples */}
            {profile.gallery && profile.gallery.length > 0 && (
                <section className="px-4 py-6 md:px-6 md:py-8 lg:px-8 lg:max-w-5xl lg:mx-auto border-t" style={{ borderColor: colors.colors.secondary }}>
                    <h2
                        className="text-xl md:text-2xl font-bold mb-6"
                        style={{ fontFamily: typography.heading, color: colors.colors.primary }}
                    >
                        {t('template.trade.ourWork')}
                    </h2>
                    <Gallery
                        images={profile.gallery}
                        layout="grid"
                        limit={8}
                        enableLightbox
                        aspectRatio="square"
                        gapSize={3}
                    />
                </section>
            )}

            {/* Availability */}
            {profile.availability && profile.availability.length > 0 && (
                <section className="px-4 py-6 md:px-6 md:py-8 lg:px-8 lg:max-w-5xl lg:mx-auto border-t" style={{ borderColor: colors.colors.secondary }}>
                    <h2
                        className="text-xl md:text-2xl font-bold mb-4"
                        style={{ fontFamily: typography.heading, color: colors.colors.primary }}
                    >
                        {t('template.trade.businessHours')}
                    </h2>
                    <div className="space-y-2">
                        {profile.availability.map((day) => (
                            <div
                                key={day.day_of_week}
                                className="flex justify-between py-2 border-b text-sm"
                                style={{ borderColor: colors.colors.secondary }}
                            >
                                <span className="font-medium">{day.day_name}</span>
                                <span style={{ color: colors.colors.textMuted }}>
                                    {day.is_available
                                        ? day.is_all_day
                                            ? t('template.trade.allDay')
                                            : `${day.start_time} - ${day.end_time}`
                                        : t('template.trade.closed')}
                                </span>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Social Links */}
            {profile.social_links && profile.social_links.length > 0 && (
                <section className="px-4 py-6 md:px-6 md:py-8 lg:px-8 lg:max-w-5xl lg:mx-auto border-t" style={{ borderColor: colors.colors.secondary }}>
                    <h2
                        className="text-xl md:text-2xl font-bold mb-4"
                        style={{ fontFamily: typography.heading, color: colors.colors.primary }}
                    >
                        {t('template.trade.followUs')}
                    </h2>
                    <div className="flex flex-wrap gap-3">
                        {profile.social_links.map((link) => (
                            <a
                                key={link.platform}
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium"
                                style={{
                                    backgroundColor: colors.colors.primary,
                                    color: colors.colors.secondary,
                                }}
                            >
                                {link.label || link.platform}
                            </a>
                        ))}
                    </div>
                </section>
            )}

            {/* Final CTA */}
            <section
                className="px-4 py-10 md:py-12 lg:px-8 text-center"
                style={{ backgroundColor: colors.colors.primary }}
            >
                <h2
                    className="text-2xl md:text-3xl font-bold mb-3"
                    style={{ fontFamily: typography.heading, color: colors.colors.secondary }}
                >
                    {t('template.trade.readyToStart')}
                </h2>
                <p className="mb-6 opacity-90 text-sm md:text-base" style={{ color: colors.colors.secondary }}>
                    {t('template.trade.freeQuote')}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 sm:justify-center">
                    {profile.phone && (
                        <a
                            href={`tel:${profile.phone}`}
                            className="inline-flex items-center justify-center gap-2 px-10 py-4 rounded-full font-bold text-lg"
                            style={{
                                backgroundColor: colors.colors.accent,
                                color: colors.colors.primary,
                            }}
                        >
                            📞 {t('template.trade.callNow')} {profile.phone}
                        </a>
                    )}
                    {profile.services && profile.services.length > 0 && (
                        <button
                            onClick={() => setIsBookingOpen(true)}
                            className="inline-flex items-center justify-center gap-2 px-10 py-4 rounded-full font-bold text-lg transition-transform hover:scale-105"
                            style={{
                                backgroundColor: colors.colors.primary,
                                color: colors.colors.secondary,
                            }}
                        >
                            📅 Book Now
                        </button>
                    )}
                </div>
            </section>

            {/* Footer */}
            <footer
                className="px-4 py-6 md:px-6 md:py-8 lg:px-8 text-center"
                style={{ backgroundColor: colors.colors.background }}
            >
                <a
                    href="/"
                    className="text-sm"
                    style={{ color: colors.colors.textMuted, textDecoration: 'none' }}
                >
                    {t('template.trade.poweredBy')}
                </a>
            </footer>

            {isBookingOpen && profile.services && profile.services.length > 0 && (
                <BookingModal
                    isOpen={isBookingOpen}
                    onClose={() => setIsBookingOpen(false)}
                    profile={profile}
                    services={profile.services}
                    categories={[]}
                />
            )}
        </div>
    )
}

export default TradeTemplate;
